import type {
  VoiceRecognitionActions,
  VoiceRecognitionState,
} from "../../hooks/useVoiceRecognition";

export type VoiceRecognitionProp =
  | boolean
  | (VoiceRecognitionState & VoiceRecognitionActions)
  | undefined;

/** État de repli quand aucun objet n'est fourni (même forme qu'avant l'extraction). */
type PlaceholderVoiceRecognition = {
  isSupported: boolean;
  isListening: boolean;
  interimTranscript: string;
  finalTranscript: string;
  startListening: () => void;
  stopListening: () => void;
  resetTranscript: () => void;
};

type ResolvedVoiceRecognition =
  | (VoiceRecognitionState & VoiceRecognitionActions)
  | PlaceholderVoiceRecognition;

/** Le navigateur expose-t-il l'API Web Speech ? */
export function isVoiceRecognitionSupported(
  win: Window | undefined = typeof window !== "undefined" ? window : undefined,
): boolean {
  return (
    !!win && ("webkitSpeechRecognition" in win || "SpeechRecognition" in win)
  );
}

/**
 * État vocal effectif de GeminiChatInterface :
 * - objet fourni → utilisé tel quel ;
 * - `false` → micro désactivé, même si le navigateur le supporte
 *   (avant, `false` n'étant pas un objet, il retombait sur l'auto-détection
 *   et le micro restait affiché) ;
 * - `true` / absent → auto-détection du support navigateur.
 */
export function resolveVoiceRecognitionState(
  voiceRecognition: VoiceRecognitionProp,
  isSupported: boolean = isVoiceRecognitionSupported(),
): ResolvedVoiceRecognition {
  if (typeof voiceRecognition === "object") return voiceRecognition;
  const placeholder: PlaceholderVoiceRecognition = {
    isSupported: voiceRecognition !== false && isSupported,
    isListening: false,
    interimTranscript: "",
    finalTranscript: "",
    startListening: () =>
      console.log("🎤 Voice recognition start - Internal Placeholder"),
    stopListening: () =>
      console.log("🎤 Voice recognition stop - Internal Placeholder"),
    resetTranscript: () =>
      console.log("🎤 Voice recognition reset - Internal Placeholder"),
  };
  return placeholder;
}
